/**
 * Custom element for handling the shipping charge form.
 *
 * This class represents a custom HTML element <product-shipping-charge-custom> that
 * manages the state of a shipping charge form, including storing and setting
 * values related to product variants and charge values.
 */
class ProductShippingChargeCustom extends HTMLElement {
  constructor() {
    super()

    /** @type {HTMLFormElement | null} */
    this.form = null

    /** @type {string | null} The variant value of the product. */
    this.variantValue = null

    /** @type {number | null} The charge value for the product. */
    this.chargeValue = null

    /** @type {HTMLFormElement | null} The form element associated with the shipping charge */
    this.variantForm = null

    /** @type {HTMLInputElement | null} Input element for storing variant value in the form. */
    this.variantInput = null

    /** @type {boolean} Whether the form has been filled in. */
    this.filledIn = false

    /** @type {ProductFormCustom} */
    this.productFormCustom = null

    // Initialize the form and input elements
    const form = this.querySelector('[data-component-product-shipping-charge-custom]')
    if (form instanceof HTMLFormElement) {
      this.setForm(form)
    }

    const selectElement = this.form.querySelector('[data-component-shipping-charge-select]')
    if (selectElement instanceof HTMLSelectElement) {
      this.setSelectElement(selectElement)
    }

    const productFormCustom = document.querySelector('product-form-custom')
    if (productFormCustom) {
      this.setProductFormCustom(productFormCustom)
    }

    const variantForm = this.querySelector('[data-component-product-shipping-charge-custom]')
    if (variantForm) {
      this.setVariantForm(variantForm)
    }
  }

  /** Lifecycle method that is called when the element is added to the DOM. */
  connectedCallback() {
    this.setSelectListener()
  }

  /**
   * Sets the form element.
   * @param {HTMLFormElement} form - The form element to set.
   */
  setForm(form) {
    this.form = form
  }

  /**
   * Sets the select element.
   * @param {HTMLSelectElement} selectElement - The select element to set.
   */
  setSelectElement(selectElement) {
    this.selectElement = selectElement
  }

  /**
   * Sets the product form custom element.
   * @param {HTMLElement} productFormCustom - The product form custom element to set.
   */
  setProductFormCustom(productFormCustom) {
    this.productFormCustom = productFormCustom
  }

  /**
   * Sets the variant form.
   * @param {HTMLElement} variantForm - The variant form to set.
   */
  setVariantForm(variantForm) {
    this.variantForm = variantForm
  }

  /**
   * Sets the select listener.
   */
  setSelectListener() {
    this.selectElement.addEventListener('change', this.handleSelectChange.bind(this))
  }

  /**
   * Handles the select change event.
   */
  handleSelectChange() {
    this.productFormCustom.updateSubTotal()
  }

  /**
   * Selects  the option within the select element based on the given variant id
   * @param {string} variantId - The variant ID to select.
   */
  selectOption(variantId) {
    this.selectElement.value = variantId
    this.setProductVariantValue(variantId)
  }

  /**
   * Selects the default option within the select element.
   */
  selectDefaultOption() {
    this.selectElement.selectedIndex = 0
    const event = new Event('change', { bubbles: true })
    this.selectElement.dispatchEvent(event)
  }

  /**
   * Reselects the current variant.
   */
  reselectCurrentVariant() {
    if (this.variantValue && this.selectElement) {
      this.selectElement.value = this.variantValue
    }
  }

  /**
   * Sets the variant value for the product.
   * @param {string} variantValue - The variant value to set.
   */
  setProductVariantValue(variantValue) {
    this.variantValue = variantValue
  }

  /**
   * Sets the charge value for the product.
   * @param {number} chargeValue - The charge value to set.
   */
  setProductChargeValue(chargeValue) {
    this.chargeValue = chargeValue
  }

  /**
   * Sets both variant and charge values using an object.
   * @param {{variantValue: string, chargeValue: number}} object - Object containing the variant and charge values.
   */
  setProductValues(object) {
    this.setProductVariantValue(object.variantValue)
    this.setProductChargeValue(object.chargeValue)
  }

  /**
   * Updates the input element with the stored variant value.
   */
  setProductValuesInInputs() {
    if (this.variantInput) {
      this.variantInput.value = this.variantValue
      this.filledIn = true
    }
  }

  /**
   * Retrieves the charge value.
   * @returns {number | null} The charge value for the product.
   */
  chargeValue() {
    return this.chargeValue
  }
}

customElements.define('product-shipping-charge-custom', ProductShippingChargeCustom)
